import { alpha, PaletteMode } from '@mui/material';
import type { ComponentTokens } from '../components';
import type { TokenOverrides } from '../index';
import {
  derivedColors,
  EXO_FONTS_STYLESHEET,
  exoColors,
  exoFonts,
  exoFontSizes,
  exoOpacity,
  exoPalette,
  exoRadius,
  exoShadowOffsets,
  exoSpacing,
  exoTextStyles,
} from './exosphere';
import { getBoomiShadowSteps } from './semantic';

// Exosphere sets corners per component: controls use spacing-2x-small, menus
// radius-standard, cards four times radius-small and dialogs radius-large.
const CONTROL_RADIUS = `${exoSpacing['2x-small']}px`;
const MENU_RADIUS = `${exoRadius.standard}px`;
const DIALOG_RADIUS = `${exoRadius.large}px`;
const CARD_RADIUS = `${4 * exoRadius.small}px`;

const largeLabel = exoTextStyles['label-x-large-bold'];
const bodyMedium = exoTextStyles['body-medium'];

export const getBoomiComponentOverrides = (
  mode: PaletteMode,
): TokenOverrides<ComponentTokens> => {
  const exo = exoColors[mode];
  const masthead = exoColors.light;
  const isLight = mode === 'light';
  const shadowSteps = getBoomiShadowSteps(mode);
  const inputHoverShadow = exo['shadow-blue-outline-200'];
  const inputFocusShadow = `inset 0 0 0 ${exoSpacing['4x-small']}px ${exo['background-selected']}, ${exo['shadow-blue-outline-300']}`;
  const inputErrorFocusShadow = `inset 0 0 0 ${exoSpacing['4x-small']}px ${exo['border-danger-strong']}, ${exo['shadow-red-outline-300']}`;
  // Exosphere Button, base flavor: contained is primary, outlined is secondary
  // and text is tertiary. Buttons in MUI's secondary color take the branded
  // flavor below; contained secondary is drawn as a branded tertiary.
  const tertiaryButton = {
    bg: 'transparent',
    fg: exo['background-selected'],
    hoverBg: exo['background-action-secondary-hover'],
    hoverFg: exo['background-selected-hover'],
    pressedBg: exo['background-tertiary'],
    disabledBg: 'transparent',
    disabledFg: exo['background-disabled'],
    disabledIcon: exo['background-disabled'],
  };
  // In dark, Exosphere's secondary surface (gray-90) is darker than our page
  // (gray-80), so outlined buttons and toggle tiles are transparent and match
  // whatever surface they sit on. In light both are white.
  const secondarySurface = isLight
    ? exo['background-action-secondary']
    : 'transparent';
  const secondaryButton = {
    bg: secondarySurface,
    border: exo['background-selected'],
    fg: exo['background-selected'],
    hoverBg: exo['background-action-secondary-hover'],
    hoverBorder: exo['background-selected-hover'],
    hoverFg: exo['background-selected-hover'],
    pressedBg: exo['background-tertiary'],
    disabledBg: secondarySurface,
    disabledFg: exo['background-disabled'],
    disabledBorder: exo['background-disabled'],
    disabledIcon: exo['background-disabled'],
  };

  // The Exosphere branded flavor: navy in light, navy-30 in dark. Exosphere's
  // dark icon color (blue-30) is not its dark branded button color.
  const brandedColor = exo['background-action'];
  const lineageType = (color: string) => ({
    border: color,
    background: alpha(color, exoOpacity['1']),
  });
  // Branded buttons are for generic actions (Cancel, Close, Filter, Sort,
  // Settings, menus); base stays for actions that create or change something.
  const brandedTertiary = {
    bg: 'transparent',
    fg: brandedColor,
    hoverBg: exo['background-action-hover-weak'],
    hoverFg: exo['background-action-hover'],
    pressedBg: exo['background-tertiary'],
    disabledBg: 'transparent',
    disabledFg: exo['background-disabled'],
    disabledIcon: exo['background-disabled'],
  };
  const brandedSecondary = {
    ...brandedTertiary,
    bg: secondarySurface,
    border: exo.border,
    hoverBorder: exo['background-action-hover'],
    disabledBg: secondarySurface,
    disabledBorder: exo['background-disabled'],
  };
  // An active quick filter is selected, as in the Exosphere segmented control.
  const quickFilterActive = {
    border: exo['background-selected'],
    bg: exo['background-selected-weak'],
    fg: exo['background-selected'],
  };

  return {
    iconButton: {
      root: { borderRadius: CONTROL_RADIUS },
      // Branded is Exosphere's default icon button flavor, and its disabled
      // state is a solid gray rather than a faded icon.
      defaultColor: brandedColor,
      disabledColor: exo['background-disabled'],
      disabledOpacity: 1,
      solidSettingsIcon: true,
    },
    widgetButton: { ...brandedTertiary, border: 'none' },
    textInputWithButton: {
      action: {
        bg: secondaryButton.bg,
        fg: secondaryButton.fg,
        border: secondaryButton.border,
        hoverBg: secondaryButton.hoverBg,
        hoverFg: secondaryButton.hoverFg,
        hoverBorder: secondaryButton.hoverBorder,
        disabledBg: secondaryButton.disabledBg,
        disabledFg: secondaryButton.disabledFg,
        disabledBorder: secondaryButton.disabledBorder,
      },
    },
    // The side nav is the Exosphere menu bar surface.
    navbar: {
      background: exo.background,
      border: exo.border,
      // The Exosphere left menubar draws unselected icons in
      // background-deselected; the selected item keeps the icon color.
      icon: exo['background-deselected'],
      selectedIcon: exo.icon,
      // The community button is not AI, so it leaves pink for an Exosphere
      // outlined button.
      community: {
        border: exo['background-selected'],
        text: exo['background-selected'],
        background: secondarySurface,
        icon: exo['background-selected'],
        radius: CONTROL_RADIUS,
      },
      cloudTooltip: { shadow: shadowSteps['300'] },
    },
    // The Exosphere masthead: one brand bar in every environment, so
    // production is marked by the environment menu's badge. The logo tile
    // drops its pink, which is kept for AI. Boomi keeps the bar dark in both
    // modes, so it always takes the light-mode colors.
    topBar: {
      background: masthead['background-brand'],
      productionBackground: masthead['background-brand'],
      border: derivedColors.light['derived/border-on-brand'],
      logoBackground: masthead['background-brand'],
      // The logo tile is the bar's color, so a divider separates it from the
      // navbar toggle, like the bar's other sections.
      logoDivider: true,
      hoverBackground: masthead['background-action-hover'],
      text: masthead['font-inverse'],
      icon: masthead['icon-inverse'],
      // The AI avatar sits on an Exosphere avatar disc (navy-10 with navy-80),
      // like the user avatar next to it.
      avatarBackground: exoPalette['navy-10'],
      avatarIcon: exoPalette['navy-80'],
      // The light-mode primary chip, so the badge reads on the bar in dark.
      productionChip: {
        fg: masthead.font,
        bg: masthead['background-selected-weak'],
      },
    },
    treeView: {
      // Exosphere trees draw node icons, folders and chevrons in
      // background-deselected (gray-60, navy-30 in dark).
      folderIcon: exo['background-deselected'],
      icon: exo['background-deselected'],
    },
    // Exosphere Wizard colors on our step layout.
    wizard: {
      selectedBackground: exo['background-selected-weak'],
      hoverBackground: exo['background-action-hover-weak'],
      selectedHoverBackground: exo['background-selected-weak'],
      doneIndex: exo['background-deselected'],
    },
    // Steppers change what is shown, so their minus and plus are base (blue),
    // like the selected lineage direction next to them.
    incrementSelector: { icon: exo['background-selected'] },
    // The days-back picker is a secondary button, so its label and chevron
    // take the button's color.
    daysBackPicker: { label: 'inherit', chevron: 'currentColor' },
    // Filtering and exporting are generic actions, so toolbar buttons are
    // branded, like the quick filters; an active quick filter stays selected
    // blue.
    toolbar: { buttonColor: 'secondary' },
    button: {
      radius: CONTROL_RADIUS,
      labelColoredIcons: true,
      tile: { icon: brandedColor, disabledIcon: exo['background-disabled'] },
      quickFilterActive,
      contained: {
        primary: {
          fg: exo['font-inverse'],
          pressedBg: exo['background-action-hover'],
          disabledBg: exo['background-disabled'],
          disabledFg: exo['font-inverse'],
          disabledIcon: exo['font-inverse'],
        },
        secondary: brandedTertiary,
        error: {
          hoverBg: exo['background-danger-strong-hover'],
          hoverFilter: 'none',
        },
      },
      outlined: { primary: secondaryButton, secondary: brandedSecondary },
      text: {
        primary: {
          hoverBg: tertiaryButton.hoverBg,
          hoverFg: tertiaryButton.hoverFg,
          pressedBg: tertiaryButton.pressedBg,
        },
        secondary: brandedTertiary,
      },
    },
    // AI actions are the Exosphere tertiary button with the AI icon, as
    // Exosphere draws its AI icon buttons; pink stays the AI exception.
    aiButton: {
      gradientBorder: false,
      bg: 'transparent',
      hoverBg: tertiaryButton.hoverBg,
      pressedBg: tertiaryButton.pressedBg,
    },
    // The running-tool shimmer is the chat surface, so it dims the label as it
    // passes and stays invisible on the panel in both modes.
    aiChat: {
      modelPillPressedBg: tertiaryButton.pressedBg,
      toolShimmer: alpha(exo['background-secondary'], exoOpacity['5']),
      // Suggested prompts are the Exosphere gray interactive Pill.
      suggestion: {
        bg: secondarySurface,
        border: exo.border,
        fg: exo.font,
        hoverBg: exo['background-action-hover-weak'],
      },
      // The environment chip opens a menu, so it is a branded tertiary button.
      // It sits on the gray composer surface, so in light its hover steps to
      // the next gray rather than the hover gray of white surfaces.
      envChip: {
        label: 'inherit',
        color: brandedTertiary.fg,
        icon: brandedTertiary.fg,
        hoverBg: isLight ? exo['background-tertiary'] : brandedTertiary.hoverBg,
        pressedBg: isLight ? '' : brandedTertiary.pressedBg,
      },
    },
    globalStyle: {
      fontFeatureSettings: 'normal',
      fontStylesheet: EXO_FONTS_STYLESHEET,
      pageBackground: exo.background,
      textColor: exo.font,
      focusRing: exo['background-selected'],
      honorReducedMotion: true,
    },
    // Exosphere h1 to h7 are regular Poppins; h6 stays a bold Noto Sans label.
    typography: {
      displayHeadingWeight: exoFonts.weightRegular,
      headingWeight: exoFonts.weightRegular,
      caption: { color: exo['font-secondary'] },
    },
    formControl: {
      label: exo['font-secondary'],
    },
    input: {
      radius: CONTROL_RADIUS,
      outlineBorder: exo['border-contrast'],
    },
    // Exosphere Input: blue border and soft blue glow on hover, a blue ring
    // and deeper glow on focus, a weak danger fill and red ring when invalid.
    textField: {
      input: {
        radius: CONTROL_RADIUS,
        outlineRadius: CONTROL_RADIUS,
        disabledBackground: exo.background,
        disabledColor: exo['font-secondary'],
        focusedOutline: exo['background-selected'],
        hoverOutline: exo['background-selected'],
        focusedBorder: exo['background-selected'],
        errorBorder: exo['border-danger-strong'],
        errorFocusedOutline: exo['border-danger-strong'],
        errorHoverOutline: exo['border-danger-strong'],
        errorBackground: exo['background-danger-weak'],
        errorFocusedBackground: 'transparent',
        hoverShadow: inputHoverShadow,
        focusShadow: inputFocusShadow,
        errorFocusShadow: inputErrorFocusShadow,
        disabledOutline: `1px solid ${exo.border}`,
        disabledTextFill: exo['font-secondary'],
        disabledPlaceholder: exo['background-disabled'],
      },
    },
    dropDown: {
      chevron: {
        color: exo['background-deselected'],
        disabledColor: exo['background-disabled'],
      },
      select: {
        borderRadius: CONTROL_RADIUS,
        focusedBorder: exo['background-selected'],
        hoverBorder: exo['background-selected'],
        errorBorder: exo['border-danger-strong'],
        errorHoverBorder: exo['border-danger-strong'],
        disabledBorder: exo.border,
        disabledBackground: exo.background,
        disabledTextColor: exo['font-secondary'],
        hoverShadow: inputHoverShadow,
        focusShadow: inputFocusShadow,
        errorBackground: exo['background-danger-weak'],
        errorFocusedBackground: exo.background,
        errorFocusShadow: inputErrorFocusShadow,
      },
      menuPaper: { borderRadius: MENU_RADIUS },
      menuItem: {
        borderRadius: CONTROL_RADIUS,
        hoverBackground: exo['background-action-hover-weak'],
        selectedBackground: exo['background-selected-weak'],
        selectedHoverBackground: exo['background-action-hover-weak'],
      },
    },
    // Exosphere Checkbox and Radio, with the selection blue in place of the
    // blue gradient they fill checked controls with.
    checkbox: {
      border: exo['background-deselected'],
      uncheckedBg: exo.page,
      checkedBg: exo['background-selected'],
      mark: exo['icon-inverse'],
      boxRadius: `${exoRadius.small - 0.625}px`,
      checkedBoxRadius: `${exoRadius.small}px`,
      disabledBorder: exo['background-disabled'],
      disabledCheckedBg: exo['background-disabled'],
      disabledMark: exo.page,
    },
    radio: {
      ring: exo['background-deselected'],
      checked: exo['background-selected'],
      disabledRing: exo['background-disabled'],
      disabledChecked: exo['background-disabled'],
      disabledOpacity: 1,
    },
    // Exosphere Toggle, without the faint sheen it lays over the "on" track.
    switch: {
      root: {
        thumbColor: exo['background-deselected'],
        switchBaseCheckedColor: exo.background,
        checkedTrackBackgroundColor: exo['background-selected'],
        focusVisibleThumbColor: exo['background-selected'],
        focusVisibleThumbBorder: exo.background,
        disabledThumbColor: exo['background-deselected'],
        checkedDisabledThumbColor: exo.background,
        trackBackgroundColor: exo['background-action-secondary'],
        trackShadow: `inset 0 0 0 ${exoSpacing['4x-small']}px ${exo.border}`,
      },
    },
    formControls: {
      errorText: {
        fontSize: `${exoFontSizes.micro}px`,
        lineHeight: `${exoFontSizes.micro}px`,
      },
    },
    formField: {
      errorText: {
        fontSize: exoTextStyles['label-micro'].fontSize,
        lineHeight: String(exoTextStyles['label-micro'].lineHeight),
      },
    },
    muiShape: {
      borderRadius: exoRadius.standard,
    },
    // Exosphere spinners stay navy while primary buttons are blue.
    spinner: {
      color: exo['background-action'],
    },
    // Exosphere Link: underlined only on hover or focus. Label links and
    // clickable breadcrumb ancestors take the link color too.
    link: {
      color: exo['font-link'],
      hoverColor: exo['font-link-hover'],
      underlineOnlyOnHover: true,
      labelColor: exo['font-link'],
      labelHoverColor: exo['font-link-hover'],
      breadcrumbColor: exo['font-link'],
      treeToggleIcon: brandedColor,
    },
    menu: {
      shadow: shadowSteps['400'],
      radius: MENU_RADIUS,
      item: {
        borderRadius: CONTROL_RADIUS,
        hoverBackground: exo['background-action-hover-weak'],
        selectedBackground: exo['background-selected-weak'],
        selectedHoverBackground: exo['background-selected-weak'],
        plainSelectedBackground: exo['background-selected-weak'],
      },
    },
    // Exosphere Tab: 13px labels with 8px around them and 4px between tabs,
    // a rounded hover with a gray underline, and a selected bar as wide as the
    // label.
    tabs: {
      indicator: exo['background-selected'],
      verticalHoverRadius: `0 ${CONTROL_RADIUS} ${CONTROL_RADIUS} 0`,
      fontSize: `${exoFontSizes['x-small']}px`,
      // The label is centered in the 32px hover background; the bottom
      // padding also holds the gap and the 2px underline below it.
      padding: `${exoSpacing['x-small']}px ${exoSpacing['x-small']}px ${
        exoSpacing['x-small'] + exoSpacing['2x-small'] + exoSpacing['3x-small']
      }px`,
      gap: `${exoSpacing['2x-small']}px`,
      hoverBackground: exo['background-action-hover-weak'],
      hoverRadius: CONTROL_RADIUS,
      hoverUnderline: exo.border,
      hoverGap: `${exoSpacing['2x-small']}px`,
      selectedHoverBackground: exo['background-selected-weak'],
      // Exosphere tabs are as wide as their label and padding.
      minWidth: '0',
      indicatorInset: `${exoSpacing['x-small']}px`,
    },
    // Exosphere Segmented Control: the selected segment is blue, since red
    // means failure in Exosphere. Text toggle groups sit on its track.
    segmentedButton: {
      toggleIconButton: {
        borderRadius: CONTROL_RADIUS,
        hoverBorder: exo['background-selected'],
        selectedBackground: exo['background-selected-weak'],
        selectedBorder: exo['background-selected'],
        selectedShadow: 'none',
        selectedShadowSmall: 'none',
        selectedShadowMedium: 'none',
      },
      toggleButtonGroup: {
        groupedBorderRadius: CONTROL_RADIUS,
        selectedBorder: exo['background-selected'],
      },
    },
    lineage: {
      // Exosphere nodes are neutral cards. A node type takes an Exosphere data
      // color as its outline, with the same color at opacity-1 as its fill;
      // green stays for passing tests, periwinkle for AI and purple for the
      // critical-asset marker. Test results use the Exosphere status pairs.
      // The Exosphere edge color (navy-50, the same in both modes) at
      // opacity-6, so dense graphs build up softly; highlighted edges stay
      // solid.
      edge: alpha(exoPalette['navy-50'], exoOpacity['6']),
      category: {
        source: lineageType(exo['data-solid-blue']),
        exposure: lineageType(exo['data-solid-coral']),
        semantic: lineageType(exo['data-solid-navy']),
        failing: {
          border: exo['border-danger-strong'],
          background: exo['background-danger-weak'],
        },
        warning: {
          border: exo['background-warning-strong'],
          background: exo['background-warning'],
        },
        passing: {
          border: exo['background-success-strong'],
          background: exo['background-success'],
        },
        // border-secondary, the Exosphere node outline, is too faint next to
        // the colored node types.
        neutral: {
          border: exo['border-contrast'],
          background: exo.background,
        },
      },
      nodeMenuItem: {
        text: exo.font,
        icon: exo.icon,
        hoverBackground: exo['background-action-hover-weak'],
        radius: CONTROL_RADIUS,
      },
      controls: {
        icon: brandedColor,
        disabledIcon: exo['background-disabled'],
      },
      streamToggle: {
        track: exo.border,
        segment: exo['background-action-secondary'],
        hoverSegment: exo['background-action-hover-weak'],
        selectedSegment: exo['background-selected-weak'],
        icon: brandedColor,
        selectedIcon: exo['background-selected'],
        disabledIcon: exo['background-disabled'],
        radius: CONTROL_RADIUS,
      },
    },
    // Exosphere Table: selected rows get a blue bar and tint, and the header
    // an underline.
    table: {
      headerDivider: exo['background-action'],
      clickableTableRow: {
        selectedBackground: exo['background-selected-weak'],
        selectedIndicator: exo['background-selected'],
      },
    },
    // An applied filter is the Exosphere Pill, as the Exosphere Filter draws
    // it: a gray outline on the surface, fully rounded, with a branded remove
    // button and no divider before it.
    filterChip: {
      grayText: { color: exo['font-secondary'] },
      wrapper: {
        backgroundColor: secondarySurface,
        border: exo.border,
        borderRadius: `${exoRadius['x-large']}px`,
      },
      closeButtonWrapper: { borderLeft: 'transparent' },
    },
    // The monitor filters are drawn as the toolbar quick filters.
    filterPill: {
      radius: CONTROL_RADIUS,
      border: `1px solid ${brandedSecondary.border}`,
      bg: brandedSecondary.bg,
      fg: brandedSecondary.fg,
      activeBorder: `1px solid ${quickFilterActive.border}`,
      activeBg: quickFilterActive.bg,
      activeFg: quickFilterActive.fg,
      hoverOpacity: 1,
      hoverBg: brandedSecondary.hoverBg,
      hoverBorder: brandedSecondary.hoverBorder,
      hoverFg: brandedSecondary.hoverFg,
    },
    // Exosphere Badge: neutral text on a weak status background. Exosphere has
    // no purple badge, so merged uses the blue one.
    chip: {
      // Noto Sans sits low in a `normal` line height. Whole pixels that leave
      // an even gap in the 28px, 18px and 24px chips keep the label centered
      // on the pixel grid.
      lineHeight: '20px',
      smallLineHeight: '16px',
      mediumLineHeight: '16px',
      colors: {
        error: { fg: exo.font, bg: exo['background-danger-weak'] },
        warning: { fg: exo.font, bg: exo['background-warning'] },
        success: { fg: exo.font, bg: exo['background-success'] },
        idle: { fg: exo.font, bg: exo['background-selected-weak'] },
        merged: { fg: exo.font, bg: exo['background-selected-weak'] },
        primary: { fg: exo.font, bg: exo['background-selected-weak'] },
        default: { fg: exo.font, bg: exo['background-tertiary'] },
      },
    },
    alert: {
      radius: CONTROL_RADIUS,
      standardInfo: {
        background: exo['surface-spheric-background'],
        iconFill: exo['background-info-strong'],
      },
      inline: {
        background: exo['surface-spheric-background'],
        text: exo.font,
        success: exo['background-success-strong'],
        info: exo['background-info-strong'],
        warning: exo['background-warning-strong'],
        error: exo['background-danger-strong'],
      },
      // The Exosphere alert banner: full width, no side or top border.
      page: { bottomBorder: `${exoSpacing['3x-small']}px` },
      closeIcon: { color: brandedColor },
    },
    // Exosphere Alert Toast. Info is the navy brand surface with inverse text;
    // the statuses are their weak surface with a strong border and glow.
    snackbar: {
      radius: CONTROL_RADIUS,
      infoByDefault: true,
      toast: {
        info: {
          background: exo['surface-spheric-brand'],
          border: exo['background-info-strong'],
          text: exo['font-inverse'],
          shadow: `${exo['shadow-weak']} ${exoShadowOffsets['500']} 0`,
        },
        success: {
          background: exo['surface-spheric-success'],
          border: exo['background-success-strong'],
          text: exo.font,
          shadow: exo['shadow-green-outline-500'],
        },
        warning: {
          background: exo['surface-spheric-warning'],
          border: exo['background-warning-strong'],
          text: exo.font,
          shadow: exo['shadow-yellow-outline-500'],
        },
        error: {
          background: exo['surface-spheric-danger-weak'],
          border: exo['background-danger-strong'],
          text: exo.font,
          shadow: exo['shadow-red-outline-500'],
        },
      },
      backdropFilter: 'blur(10px)',
      message: {
        successColor: exo.font,
        warningColor: exo.font,
        errorColor: exo.font,
        infoColor: exo['font-inverse'],
      },
    },
    toggleButton: {
      root: {
        color: exo.font,
        borderColor: exo.border,
        borderRadius: CONTROL_RADIUS,
        background: secondarySurface,
        neutralHoverBackground: exo['background-action-hover-weak'],
        selectedBackground: exo['background-selected-weak'],
        selectedHoverBackground: exo['background-selected-weak'],
        selectedColor: exo['background-selected'],
      },
    },
    // Exosphere selects draw the chevron as a plain suffix icon, with no
    // button hover of its own.
    autocomplete: {
      shadow: shadowSteps['400'],
      radius: MENU_RADIUS,
      suggestionItem: { borderRadius: CONTROL_RADIUS },
      actionItem: { borderRadius: CONTROL_RADIUS },
      option: { borderRadius: CONTROL_RADIUS },
      popupIndicator: {
        color: exo['background-deselected'],
        hoverBackground: 'transparent',
        disabledColor: exo['background-disabled'],
      },
    },
    // Exosphere Dialog: the title sits on the dialog surface; content text
    // takes the size of the matching role in Exosphere's own dialogs.
    dialog: {
      radius: DIALOG_RADIUS,
      title: {
        backgroundColor: 'transparent',
        fontSize: largeLabel.fontSize,
        fontWeight: largeLabel.fontWeight,
        lineHeight: String(largeLabel.lineHeight),
      },
      message: {
        fontSize: bodyMedium.fontSize,
      },
    },
    // Exosphere Side Drawer.
    drawer: {
      closeIcon: { fill: brandedColor },
      paper: {
        shadow: `0 ${exoSpacing['3x-small']}px ${exoSpacing.standard}px ${exo['shadow-moderate']}`,
        radius: '0',
      },
      wrapper: { borderRadius: '0' },
    },
    card: {
      radius: CARD_RADIUS,
    },
    illustration: {
      tile: exo['background-secondary'],
      line: exo.icon,
    },
    // A light or dark editor on the Exosphere JSON editor surfaces. Exosphere
    // has no SQL colors, so keywords take the tertiary text color and the
    // other syntax colors avoid the AI and status hues.
    codeEditor: {
      aceTheme: '',
      radius: MENU_RADIUS,
      colors: {
        background: exo.background,
        gutter: exo['background-secondary'],
        gutterText: exo['font-secondary'],
        border: exo.border,
        disabledBorder: exo['border-secondary'],
        disabledGutterText: exo['background-disabled'],
        indentGuide: `inset -1px 0 0 ${exo['border-secondary']}`,
        activeLine: exo['background-tertiary'],
        activeGutter: isLight ? exoPalette['blue-20'] : exoPalette['blue-70'],
        selection: isLight
          ? alpha(exoPalette['blue-20'], exoOpacity['4'])
          : alpha(exoPalette['blue-70'], exoOpacity['3']),
        text: exo.font,
        secondaryText: exo['font-secondary'],
        keyword: exo['font-tertiary'],
        function: isLight ? exoPalette['purple-60'] : exoPalette['purple-30'],
        string: isLight ? exoPalette['aqua-70'] : exoPalette['aqua-40'],
        number: isLight ? exoPalette['yellow-70'] : exoPalette['yellow-40'],
      },
      toolbar: {
        background: exo.background,
        border: `1px solid ${exo.border}`,
        disabledBorder: `1px solid ${exo['border-secondary']}`,
        radius: CONTROL_RADIUS,
        buttonRadius: CONTROL_RADIUS,
        buttonHoverBackground: exo['background-action-secondary-hover'],
      },
      chat: {
        background: exo.background,
        border: `1px solid ${exo.border}`,
        radius: MENU_RADIUS,
        bar: exo['background-secondary'],
        barDivider: `1px solid ${exo.border}`,
        meta: exo['font-secondary'],
        link: exo['font-link'],
        editorBorder: 'none',
        editorRadius: '0',
      },
    },
    // The Exosphere floating surface; its placeholder is the input one, not
    // an AI color.
    floatingActionsMenu: {
      bg: exo['surface-spheric-background'],
      border: `1px solid ${exo.border}`,
      dropShadow: 'none',
      shadow: shadowSteps['400'],
      itemHoverBg: exo['background-action-hover-weak'],
      placeholder: exo['font-secondary'],
    },
    // Exosphere EmptyState: a neutral icon, a large label and a secondary
    // message.
    emptyState: {
      icon: exo.icon,
      title: {
        fontSize: largeLabel.fontSize,
        fontWeight: largeLabel.fontWeight,
        lineHeight: String(largeLabel.lineHeight),
      },
      message: {
        color: exo['font-secondary'],
        fontSize: bodyMedium.fontSize,
        lineHeight: String(bodyMedium.lineHeight),
      },
      compactMessage: {
        fontSize: bodyMedium.fontSize,
      },
      pageTitle: {
        fontSize: largeLabel.fontSize,
        fontWeight: largeLabel.fontWeight,
      },
      // In dark the glass tiles are gray with faint navy outlines, the glow is
      // navy and the logo ink is light.
      darkIntegrationsGraphic: !isLight,
    },
    tooltip: {
      bg: exo['background-action-inverse'],
      color: exo['font-inverse'],
      radius: CONTROL_RADIUS,
    },
  };
};
